import {
	AsyncResult,
	FileDownloadGetApiRequestBody,
	FileDownloadGetApiResponse,
	HttpMethod,
	RedirectionStatus,
	SuccessStatus,
	err,
	isErr,
	isOk,
	ok,
} from "@jace/api";
import { EventEmitter, Unsubscribe, suuid, unwrapOr } from "@zetalabs/core";
import { batchIterate, tryPromise } from "../../utils";
import { calcProgress, isDone } from "../common";
import { ComputedValue } from "../common/computed-value";
import { parseUnifiedUri } from "../common/parse-unified-uri";
import { SubjectHash } from "../hash";
import { FileManager } from "../manager";
import {
	EventPayload,
	FileDownload,
	FileManagerConfiguration,
	ProcessingStatus,
	Session,
	SessionEvent,
	SessionManager,
	SessionType,
	TerminationStatus,
} from "../types";

interface FileMetadata {
	size: number;
	type: string;
}

type FileDownloadConfiguration = FileManagerConfiguration["downloads"] & {
	maxPreloadSize: number;
};

const defaultFileDownloadConfig: FileDownloadConfiguration = {
	concurrent: 5,
	maxPreloadSize: 300 * 1024,
};

export class DownloadSessionManager implements SessionManager {
	public readonly config: FileDownloadConfiguration;

	private _items = new Map<string, FileDownload>();
	private _queued = new Set<string>();

	private _request: ReturnType<typeof setTimeout> | null = null;
	private _controllers = new Map<string, AbortController>();

	private _em = new EventEmitter<SessionEvent>();

	private _state: ComputedValue<
		Session<FileDownload, FileDownloadConfiguration>
	>;

	constructor(
		public readonly subject: SubjectHash,
		public readonly manager: FileManager,
		config: Partial<FileDownloadConfiguration> = {},
	) {
		this.manager = manager;
		this.subject = subject;
		this.config = Object.assign(
			{},
			defaultFileDownloadConfig,
			manager.config.downloads,
			config,
		);

		this._state = new ComputedValue(() => {
			const items = this.toArray();
			const progress = calcProgress(items);
			const status = isDone(items)
				? TerminationStatus.Done
				: ProcessingStatus.InProgress;
			const now = Date.now();

			return {
				subject: this.subject,
				items,
				status,
				progress,
				errors: null,
				config: this.config,
				updatedAt: now,
			};
		});
	}

	public get session(): Session<FileDownload, FileDownloadConfiguration> {
		// NOTE: This is a bit extra at the moment...
		this._state.invalid = true;
		return this._state.value;
	}

	public download(uri: string): FileDownload;
	public download(uri: string[]): FileDownload[];
	public download(uri: string | string[]): FileDownload | FileDownload[] {
		if (Array.isArray(uri)) {
			return uri.map((uri) => {
				const result = parseUnifiedUri(uri);

				if (isErr(result)) {
					throw new Error("Invalid unified uri");
				}

				const { name, ext } = result.value;

				const download: FileDownload = {
					ext,
					name,
					id: suuid(),
					progress: 0,
					error: null,
					type: SessionType.Download,
					unifiedUri: uri,
					source: null,
					status: TerminationStatus.Idle,
				};

				// TODO: Might want to add a batch queue here
				this.queue(download);

				return download;
			});
		} else {
			const result = parseUnifiedUri(uri);

			if (isErr(result)) {
				throw new Error("Invalid unified uri");
			}

			const { name, ext } = result.value;

			const download: FileDownload = {
				id: suuid(),
				name,
				ext,
				progress: 0,
				error: null,
				type: SessionType.Download,
				unifiedUri: uri,
				source: null,
				status: TerminationStatus.Idle,
			};

			this.queue(download);

			return download;
		}
	}

	public delete = async (id: string): Promise<void> => {
		const controller = this._controllers.get(id);

		if (controller) {
			controller.abort();
		}

		const item = this._items.get(id);

		if (item) {
			item.status = TerminationStatus.Cancelled;

			this._items.delete(id);
		}
	};

	public on<E extends SessionEvent>(
		event: E,
		callback: (data: EventPayload<E, FileDownload>) => void,
	): Unsubscribe {
		return this._em.on(event, callback);
	}

	public getItems = (): FileDownload[] => {
		return this.toArray();
	};

	public dispose(): void {
		this._queued.clear();

		if (this._request) {
			clearTimeout(this._request);
		}

		this._controllers.forEach((controller) => {
			controller.abort();
		});

		this._items.clear();
	}

	private queue(item: FileDownload): void {
		if (this._queued.has(item.id)) {
			return;
		}

		item.status = ProcessingStatus.Preparing;

		this._queued.add(item.unifiedUri);
		this._items.set(item.unifiedUri, item);

		if (this._request) {
			clearTimeout(this._request);
			this._request = null;
		}

		this._request = setTimeout(async () => {
			this._request = null;
			const candidates = Array.from(this._queued);
			this._queued.clear();

			await this.process(candidates);
		}, 50);
	}

	// NOTE: Check if this is better of in a generator
	private process = async (uris: string[]): Promise<void> => {
		const downloadHandler = tryPromise(async (uri: string) => {
			const item = this._items.get(uri);

			if (!item || item.status === TerminationStatus.Cancelled) {
				return;
			}

			const controller = new AbortController();
			this._controllers.set(uri, controller);

			const requestBody: FileDownloadGetApiRequestBody = {
				query: {
					unified_uri_str: uri,
				},
			};

			const result =
				await this.manager.transport.get<FileDownloadGetApiResponse>(
					"/files/download",
					{
						...requestBody,
						signal: controller.signal,
					},
				);

			if (isErr(result)) {
				// Do something here
				throw new Error(result.error.message);
			}

			const downloadUrl = result.value.url;
			item.source = downloadUrl;

			const metadataResult = await this.getMetadata(downloadUrl);

			if (isOk(metadataResult)) {
				const metadata = metadataResult.value;

				if (metadata && metadata.size < this.config.maxPreloadSize) {
					// files are too large
				}
			}

			item.status = ProcessingStatus.InProgress;

			// Download actual file
			const xhr = new XMLHttpRequest();
			xhr.open("GET", downloadUrl);
			xhr.responseType = "blob";

			xhr.onprogress = (event) => {
				item.progress = event.loaded / event.total;
				this._em.emit("change", this.session);
			};

			// Start
			xhr.onloadstart = (_event) => {
				item.progress = 0;
				this._em.emit("change", this.session);
			};

			xhr.onload = () => {
				this._controllers.delete(item.id);

				item.progress = 1;

				this._em.emit("change", this.session);

				if (
					xhr.status >= SuccessStatus.Ok &&
					xhr.status < RedirectionStatus.MultipleChoices
				) {
					item.status = TerminationStatus.Done;
					this._em.emit("change", this.session);
				}

				if (isDone(this.toArray())) {
					this._em.emit("done", this.session);
				}
			};

			xhr.onabort = () => {
				item.status = TerminationStatus.Cancelled;
				this._em.emit("change", this.session);
			};

			xhr.onerror = () => {
				item.status = TerminationStatus.Error;
				this._em.emit("change", this.session);
			};

			xhr.send();
		});

		for (const batch of batchIterate(uris, this.config.concurrent)) {
			await Promise.allSettled(batch.map(downloadHandler));
		}
	};

	private async getMetadata(uri: string): AsyncResult<FileMetadata | null> {
		try {
			const response = await fetch(uri, { method: HttpMethod.Head });

			const contentLength = response.headers.get("content-length");
			const contentType = response.headers.get("content-type");

			if (contentLength && contentType) {
				return ok({
					size: parseInt(contentLength, 10),
					type: contentType,
				});
			}

			return ok(null);
		} catch (error) {
			return err(
				unwrapOr(
					error,
					new Error("Unknown error while fetching metadata in file download"),
				),
			);
		}
	}

	private toArray = (): FileDownload[] => {
		return Array.from(this._items.values());
	};
}
